import React from 'react';
import PropTypes from 'prop-types';
import { CAM_FEATURES, subIsMonthly } from './';
import './CameraFeaturesTable.less';

export default function CameraFeaturesTable({ onClick, currentTier = 'standard', annual, user, currentDevice }) {
  // Get current subscription to check period
  const currentSubscription = React.useMemo(() => {
    if (!currentDevice || !user) return null;
    const subscriptions = user?.info?.payments?.subscriptions || [];
    return subscriptions.find(sub =>
      sub.macAddress === currentDevice.macAddress &&
      (sub.productName?.includes('tier1') || sub.productName?.includes('tier2'))
    );
  }, [currentDevice, user]);

  // Check if current subscription period matches the selected tab
  const currentIsMonthly = currentSubscription ? subIsMonthly(currentSubscription) : null;
  const periodMatches = currentIsMonthly !== null && ((annual && !currentIsMonthly) || (!annual && currentIsMonthly));

  // Only show active when both tier AND period match
  const isActive = (tier) => currentTier === tier && periodMatches;

  // Determine button label for each tier
  const getButtonLabel = (tier) => {
    // If same tier but switching from annual to monthly, it's a downgrade
    if (currentTier === tier && currentIsMonthly === false && !annual) {
      return 'Downgrade';
    }
    // If higher tier than current, it's an upgrade
    if ((tier === 'gold' && currentTier === 'silver') || (tier === 'silver' && currentTier === 'standard')) {
      return 'Upgrade';
    }
    // If lower tier than current, it's a downgrade
    if ((tier === 'silver' && currentTier === 'gold') || (tier === 'standard' && currentTier !== 'standard')) {
      return 'Downgrade';
    }
    // Default to upgrade
    return 'Upgrade';
  };
  const renderValue = (value) => {
    // Handle object with value and sub-label
    if (typeof value === 'object' && value !== null && value.value) {
      return (
        <span className="text-value">
          {value.value}
          {value.sub && <span className="sub-label">{value.sub}</span>}
        </span>
      );
    }
    // Handle boolean
    if (value === true) {
      return <span className="check"></span>;
    } else if (value === false) {
      return <span className="x">✕</span>;
    }
    // Handle string
    if (typeof value === 'string') {
      return <span className="text-value">{value}</span>;
    }
    return null;
  };

  const renderTierHeaders = () => (
    <>
      <div className="feature-label-header"></div>

      <div className="tier-header standard">
        <h3>Standard</h3>
        <h4>Free</h4>
        {isActive('standard') && <span className="active-badge">✓ Active</span>}
      </div>

      <div className="tier-header silver">
        <div className="camera-logo silver"></div>
        <h4>Camera Silver</h4>
        {isActive('silver') ? (
          <span className="active-badge">✓ Active</span>
        ) : (
          <button
            className="btn btn-primary"
            onClick={() => onClick('silver')}
          >
            {getButtonLabel('silver')}
          </button>
        )}
      </div>

      <div className="tier-header gold">
        <div className="camera-logo gold"></div>
        <h4>Camera Gold</h4>
        {isActive('gold') ? (
          <span className="active-badge">✓ Active</span>
        ) : (
          <button
            className="btn btn-primary"
            onClick={() => onClick('gold')}
          >
            {getButtonLabel('gold')}
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="payment-camera-features-table">
      <div className="features-header">
        <div className="header-row">
          {renderTierHeaders()}
        </div>
      </div>

      <div className="features-body">
        {CAM_FEATURES.map((feature, index) => (
          <div key={index} className="feature-row">
            <div className="feature-label">
              <strong>{feature.label}</strong>
              {feature.description && (
                <p className="feature-description">
                  {feature.description}
                </p>
              )}
            </div>
            <div className="feature-values">
              <div className="feature-value standard">
                {renderValue(feature.standard)}
              </div>
              <div className="feature-value silver">
                {renderValue(feature.silver)}
              </div>
              <div className="feature-value gold">
                {renderValue(feature.gold)}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="features-footer">
        <div className="footer-row">
          {renderTierHeaders()}
        </div>
        <a href="https://ambientweather.com/enhanced-weather-camera-subscription" target="_blank" rel="noopener noreferrer">
          Learn more about Enhanced Camera Subscriptions
        </a>
      </div>
    </div>
  );
}

CameraFeaturesTable.propTypes = {
  onClick: PropTypes.func.isRequired,
  currentTier: PropTypes.oneOf(['standard', 'silver', 'gold']),
  annual: PropTypes.bool,
  user: PropTypes.object,
  currentDevice: PropTypes.object
};

CameraFeaturesTable.displayName = 'CameraFeaturesTable';
